// Product Form Enhancement - Convert regular forms to AJAX
class ProductFormEnhancer {
  constructor() {
    this.init();
  }

  init() {
    // Wait for cart manager to be available
    if (typeof window.cartManager === 'undefined') {
      setTimeout(() => this.init(), 100);
      return;
    }

    // Find and enhance all product forms
    this.enhanceProductForms();
  }

  enhanceProductForms() {
    const productForms = document.querySelectorAll('form[action*="/cart/add"]');
    
    productForms.forEach(form => {
      this.enhanceForm(form);
    });
  }

  enhanceForm(form) {
    // Skip forms that don't have a variant ID (e.g., $0 products with quote buttons only).
    //
    // 2026-09-29: a form that posts items[n][id] instead of id is still a real cart
    // form. "Add both to cart" on the item page uses that shape, and so does the main
    // buy form whenever an add-on is offered. They were skipped here, so nothing
    // intercepted the submit, the browser posted to /cart/add for real, and Shopify
    // answered with its default 302 to /cart. That is why those buttons left the page
    // instead of opening the drawer.
    const variantIdInput = form.querySelector('input[name="id"], input[name="items[0][id]"]');
    if (!variantIdInput) {
      return; // This form doesn't have a variant to add, skip enhancement
    }
    
    // Add event listener to intercept form submission.
    //
    // 2026-09-30: honour a cancel from a listener that ran before this one.
    // The item page's own submit handler calls preventDefault() when the shopper
    // has not picked a required size, or left a required custom name or photo
    // empty. preventDefault() only stops the browser's native POST; it does not
    // stop other listeners. This one is attached at DOMContentLoaded, so it runs
    // second, and it used to add the item anyway in the preselected default size
    // while the page was still telling the shopper to pick one.
    form.addEventListener('submit', (e) => {
      if (e.defaultPrevented) return;
      e.preventDefault();
      this.handleFormSubmission(form, e);
    });

    // Add loading state capability to submit button
    const submitButton = form.querySelector('input[type="submit"], button[type="submit"]');
    if (submitButton) {
      submitButton.dataset.originalText = submitButton.value || submitButton.textContent;
    }
  }

  async handleMultiItemSubmission(form, formData) {
    const items = [];
    for (let i = 0; formData.get(`items[${i}][id]`); i++) {
      items.push({
        id: formData.get(`items[${i}][id]`),
        quantity: parseInt(formData.get(`items[${i}][quantity]`)) || 1,
      });
    }
    if (!items.length) return;

    const submitButton = form.querySelector('input[type="submit"], button[type="submit"]');
    const originalText = submitButton?.dataset.originalText
      || submitButton?.dataset.was
      || (submitButton ? (submitButton.value || submitButton.textContent) : 'Add to cart');
    const setLabel = (text) => {
      if (!submitButton) return;
      if (submitButton.tagName === 'INPUT') submitButton.value = text;
      else submitButton.textContent = text;
    };

    if (submitButton) { submitButton.disabled = true; }
    setLabel('Adding...');

    try {
      const result = await window.cartManager.addMultipleToCart(items);
      if (!result.success) throw new Error(result.error || 'Failed to add to cart');

      setLabel('Added!');
      if (submitButton) submitButton.style.backgroundColor = '#16a34a';
      const noun = items.length > 1 ? 'items' : 'item';
      window.cartUI?.showSuccessNotification(`${items.length} ${noun} added to cart!`);
      if (window.cartDrawer) window.cartDrawer.openDrawer();

      setTimeout(() => {
        if (submitButton) { submitButton.disabled = false; submitButton.style.backgroundColor = ''; }
        setLabel(originalText);
      }, 2000);
    } catch (error) {
      console.error('Error adding to cart:', error);
      setLabel('Error - Try Again');
      if (submitButton) submitButton.style.backgroundColor = '#dc2626';
      window.cartUI?.showErrorNotification(error.message || 'Failed to add item to cart');
      setTimeout(() => {
        if (submitButton) { submitButton.disabled = false; submitButton.style.backgroundColor = ''; }
        setLabel(originalText);
      }, 3000);
    }
  }

  async handleFormSubmission(form, e) {
    const formData = new FormData(form);

    // items[n][id] form: hand it to the multi-add the cart manager already has,
    // then fall through to the same success handling as a single add.
    if (!formData.get('id') && formData.get('items[0][id]')) {
      return this.handleMultiItemSubmission(form, formData);
    }

    const variantId = formData.get('id');
    const quantity = parseInt(formData.get('quantity')) || 1;
    
    // Prevent $0 products from being added to cart
    // Check both the hidden input and any data attribute on the form
    const variantPriceInput = form.querySelector('[data-variant-price]');
    const variantPrice = variantPriceInput ? parseInt(variantPriceInput.dataset.variantPrice) : null;
    
    if (variantPrice !== null && variantPrice === 0) {
      window.cartUI?.showErrorNotification('This product is not available for purchase. Please contact us for pricing.');
      return;
    }
    
    // Additional safety check: if no variant ID, don't proceed
    if (!variantId) {
      window.cartUI?.showErrorNotification('Please select a product variant.');
      return;
    }
    
    // Get submit button
    const submitButton = form.querySelector('input[type="submit"], button[type="submit"]');
    const originalText = submitButton?.dataset.originalText || 'Add to cart';
    
    // Set loading state
    if (submitButton) {
      submitButton.disabled = true;
      if (submitButton.tagName === 'INPUT') {
        submitButton.value = 'Adding...';
      } else {
        submitButton.textContent = 'Adding...';
      }
    }

      try {
      // _Wholesaler is no longer written here: checkout-plus's wholesaler-line-tag extension sets it at checkout
      // (2026-10-04), so the name never sits in the page source.
      let properties = {};

      // Get metal type from metafield if available
      const metalField = document.querySelector('[data-metal-type]');
      if (metalField && metalField.dataset.metalType) {
        properties['_Metal'] = metalField.dataset.metalType;
      }

      // The listing test's tag (2026-10-31, the item listing pages room). Buy now and the wallet buttons already carry it from
      // the hidden input in pdp-item-buy.liquid, which is enabled only for a shopper a test counted; Add to cart and the
      // sticky bar build their own properties here, so without this line their orders carried no test side at all.
      const lxField = document.querySelector('input[data-lx]:not([disabled])');
      if (lxField && lxField.value) {
        properties['_lx'] = lxField.value;
      }

      // Get custom letter from anywhere on the page if available
      // There may be multiple inputs with the same ID (mobile/desktop versions), so check all
      const allLetterInputs = document.querySelectorAll('#custom-letter-input, input[name="properties[Preferred Letter]"]');
      let letterValue = null;
      
      // Find the first input that has a value
      for (const input of allLetterInputs) {
        if (input.value && input.value.trim()) {
          letterValue = input.value.trim().toUpperCase();
          console.log('Found Preferred Letter value:', letterValue, 'from input:', input.id || input.name);
          break;
        }
      }
      
      if (letterValue) {
        properties['Preferred Letter'] = letterValue;
      }

      // Get custom name from anywhere on the page if available
      const allNameInputs = document.querySelectorAll('#custom-name-input, input[name="properties[Custom Name]"]');
      let nameValue = null;

      for (const input of allNameInputs) {
        if (input.value && input.value.trim()) {
          nameValue = input.value.trim();
          break;
        }
      }

      if (nameValue) {
        properties['Custom Name'] = nameValue;
      }

      console.log('Final properties object:', properties);

      // Collect any checked add-on items (custom.addon_products metafield)
      // Each add-on gets a hidden _addon_of property linking it to the main
      // variant so the cart drawer can render it as a nested sub-item.
      const addonCheckboxes = document.querySelectorAll('.product-addon-checkbox:checked');
      const addonItems = Array.from(addonCheckboxes).map((cb) => ({
        id: cb.dataset.addonVariantId,
        quantity: 1,
        properties: { _addon_of: String(variantId) },
      }));

      // Customer photo upload (line item file property). When required by the
      // product, validate first; the validator scrolls + shows an inline
      // error if no file is selected. /cart/add.js multipart only accepts a
      // single item, so add-ons are added in a follow-up call when present.
      const photoCheck = typeof window.customerPhotoValidate === 'function'
        ? window.customerPhotoValidate()
        : { valid: true, file: null };
      if (!photoCheck.valid) {
        if (submitButton) {
          submitButton.disabled = false;
          if (submitButton.tagName === 'INPUT') submitButton.value = originalText;
          else submitButton.textContent = originalText;
        }
        return;
      }
      let photoFile = photoCheck.file;
      if (!photoFile) {
        const photoInputs = document.querySelectorAll('input[name="properties[Customer Photo]"]');
        for (const inp of photoInputs) {
          if (inp.files && inp.files[0]) { photoFile = inp.files[0]; break; }
        }
      }

      let result;
      if (photoFile) {
        const fd = new FormData();
        fd.append('id', variantId);
        fd.append('quantity', String(quantity));
        for (const [key, val] of Object.entries(properties)) {
          fd.append(`properties[${key}]`, val);
        }
        fd.append('properties[Customer Photo]', photoFile, photoFile.name);

        result = await window.cartManager.addToCartFormData(fd);

        if (result.success && addonItems.length > 0) {
          // Add-ons follow as a separate JSON multi-add.
          await window.cartManager.addMultipleToCart(addonItems);
        }
      } else if (addonItems.length > 0) {
        const items = [
          { id: variantId, quantity: quantity, properties: properties },
          ...addonItems,
        ];
        result = await window.cartManager.addMultipleToCart(items);
      } else {
        result = await window.cartManager.addToCart(variantId, quantity, properties);
      }

      if (result.success) {
        // Success state
        if (submitButton) {
          if (submitButton.tagName === 'INPUT') {
            submitButton.value = 'Added!';
          } else {
            submitButton.textContent = 'Added!';
          }
          submitButton.style.backgroundColor = '#16a34a'; // green-600
        }

        // Show success notification
        const totalAdded = quantity + addonItems.length;
        const noun = totalAdded > 1 ? 'items' : 'item';
        window.cartUI.showSuccessNotification(`${totalAdded} ${noun} added to cart!`);

        // Open cart drawer
        if (window.cartDrawer) {
          window.cartDrawer.openDrawer();
        }

        // Reset button after 2 seconds
        setTimeout(() => {
          if (submitButton) {
            submitButton.disabled = false;
            if (submitButton.tagName === 'INPUT') {
              submitButton.value = originalText;
            } else {
              submitButton.textContent = originalText;
            }
            submitButton.style.backgroundColor = ''; // Reset to original
          }
        }, 2000);

      } else {
        throw new Error(result.error || 'Failed to add to cart');
      }

    } catch (error) {
      console.error('Error adding to cart:', error);
      
      // Error state
      if (submitButton) {
        if (submitButton.tagName === 'INPUT') {
          submitButton.value = 'Error - Try Again';
        } else {
          submitButton.textContent = 'Error - Try Again';
        }
        submitButton.style.backgroundColor = '#dc2626'; // red-600
      }

      // Show error notification
      window.cartUI.showErrorNotification(error.message || 'Failed to add item to cart');

      // Reset button after 3 seconds
      setTimeout(() => {
        if (submitButton) {
          submitButton.disabled = false;
          if (submitButton.tagName === 'INPUT') {
            submitButton.value = originalText;
          } else {
            submitButton.textContent = originalText;
          }
          submitButton.style.backgroundColor = ''; // Reset to original
        }
      }, 3000);
    }
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new ProductFormEnhancer();
  });
} else {
  new ProductFormEnhancer();
} 